import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { AlfredInitiateWithdrawalTransaction, AlfredSessionSnapshot, alfredService } from "src/api";
import "../depositForm/alfred-payment-instructions.scss";

interface Props {
  sessionId: string;
  transaction: AlfredInitiateWithdrawalTransaction;
  onReset: () => void;
}

const TERMINAL_STATUSES = new Set(["FIAT_TRANSFER_COMPLETED", "ON_CHAIN_COMPLETED", "FAILED"]);
const POLL_INTERVAL_MS = 4_000;

const formatAmount = (raw: string | undefined | null): string => {
  if (raw == null || raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return n.toFixed(2);
};

const useAlfredSessionPolling = (sessionId: string, enabled: boolean): AlfredSessionSnapshot | null => {
  const [snapshot, setSnapshot] = useState<AlfredSessionSnapshot | null>(null);
  const stoppedRef = useRef(false);
  useEffect(() => {
    stoppedRef.current = false;
    if (!sessionId || !enabled) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await alfredService.getSession(sessionId);
        if (cancelled) return;
        if (data) setSnapshot(data);
        if (data?.transactionStatus && TERMINAL_STATUSES.has(data.transactionStatus)) {
          stoppedRef.current = true;
        }
      } catch (e) {
        console.warn("Alfred session poll failed:", e);
      }
    };
    tick();
    const id = setInterval(() => {
      if (stoppedRef.current) return;
      tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sessionId, enabled]);
  return snapshot;
};

const Row: React.FC<{ label: string; value: string; copyable?: boolean }> = ({ label, value, copyable }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    if (!copyable) return;
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined
    );
  };
  return (
    <div className="alfred-instructions-row">
      <span className="alfred-instructions-row__label">{label}</span>
      <span
        className={`alfred-instructions-row__value ${copyable ? "alfred-instructions-row__value--copyable" : ""}`}
        onClick={onCopy}
        title={copyable ? (copied ? "Copied!" : "Click to copy") : undefined}
      >
        {value}
      </span>
    </div>
  );
};

/**
 * OFFRAMP progression per Alfred docs:
 *   CREATED → ON_CHAIN_DEPOSIT_RECEIVED → TRADE_COMPLETED →
 *   FIAT_TRANSFER_INITIATED → FIAT_TRANSFER_COMPLETED
 */
const TX_STEPS: { key: string; labelKey: string }[] = [
  { key: "CREATED", labelKey: "WITHDRAWAL_FORM.STEP.CREATED" },
  {
    key: "ON_CHAIN_DEPOSIT_RECEIVED",
    labelKey: "WITHDRAWAL_FORM.STEP.ON_CHAIN_DEPOSIT_RECEIVED",
  },
  { key: "TRADE_COMPLETED", labelKey: "WITHDRAWAL_FORM.STEP.TRADE_COMPLETED" },
  {
    key: "FIAT_TRANSFER_INITIATED",
    labelKey: "WITHDRAWAL_FORM.STEP.FIAT_TRANSFER_INITIATED",
  },
  {
    key: "FIAT_TRANSFER_COMPLETED",
    labelKey: "WITHDRAWAL_FORM.STEP.FIAT_TRANSFER_COMPLETED",
  },
];

const TransactionStepper: React.FC<{
  status: string | null;
  failedAtStep?: string | null;
  failureReason?: string | null;
}> = ({ status, failedAtStep, failureReason }) => {
  const { t } = useTranslation();
  const isFailure = status === "FAILED";
  const currentIdx = Math.max(
    0,
    TX_STEPS.findIndex((s) => s.key === status)
  );
  const failedIdx = failedAtStep ? TX_STEPS.findIndex((s) => s.key === failedAtStep) + 1 : 0;
  // On the terminal step (`FIAT_TRANSFER_COMPLETED`) collapse "active"
  // into "done" so the last cell flips to ✓ instead of pulsing as
  // in-flight — the chain is finished.
  const isTerminalSuccess = !isFailure && status === TX_STEPS[TX_STEPS.length - 1].key;
  return (
    <>
      <ol className={`alfred-stepper ${isFailure ? "alfred-stepper--has-failure" : ""}`}>
        {TX_STEPS.map((step, i) => {
          const done = isFailure
            ? i < failedIdx
            : isTerminalSuccess
              ? i <= currentIdx
              : i < currentIdx;
          const active = !isFailure && !isTerminalSuccess && i === currentIdx;
          const failed = isFailure && i === failedIdx;
          return (
            <li
              key={step.key}
              className={[
                "alfred-stepper__step",
                done && "alfred-stepper__step--done",
                active && "alfred-stepper__step--active",
                failed && "alfred-stepper__step--failed",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="alfred-stepper__dot" aria-hidden>
                {failed ? "✕" : done ? "✓" : i + 1}
              </span>
              <span className="alfred-stepper__label">{t(step.labelKey)}</span>
            </li>
          );
        })}
      </ol>
      {isFailure && failureReason && (
        <p className="alfred-failure-reason">
          <span className="alfred-failure-reason__label">{t("WITHDRAWAL_FORM.FAILURE_REASON_LABEL")}</span>
          <span className="alfred-failure-reason__text">{failureReason}</span>
        </p>
      )}
    </>
  );
};

const StatusBanner: React.FC<{ status: string | null }> = ({ status }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const isFailure = status === "FAILED";
  const isSuccess = status === "FIAT_TRANSFER_COMPLETED";
  const cls = isFailure
    ? "alfred-status-banner--error"
    : isSuccess
      ? "alfred-status-banner--success"
      : "alfred-status-banner--info";
  return (
    <div className={`alfred-status-banner ${cls}`}>
      {t(`WITHDRAWAL_FORM.TX_STATUS.${status}`, {
        defaultValue: status.replace(/_/g, " ").toLowerCase(),
      })}
    </div>
  );
};

const CompletionModal: React.FC<{
  variant: "success" | "failure";
  amount?: string;
  currency?: string;
  reason?: string | null;
  onClose: () => void;
}> = ({ variant, amount, currency, reason, onClose }) => {
  const { t } = useTranslation();
  const isSuccess = variant === "success";
  return (
    <div className="alfred-modal-backdrop" role="dialog" aria-modal>
      <div className={`alfred-modal alfred-modal--${variant}`}>
        <div className={`alfred-modal__icon alfred-modal__icon--${variant}`} aria-hidden>
          {isSuccess ? "✓" : "✕"}
        </div>
        <h2 className="alfred-modal__title">
          {t(isSuccess ? "WITHDRAWAL_FORM.COMPLETED_TITLE" : "WITHDRAWAL_FORM.FAILED_TITLE")}
        </h2>
        {isSuccess && amount && currency && (
          <p className="alfred-modal__amount">
            {amount} {currency}
          </p>
        )}
        <p className="alfred-modal__text">
          {isSuccess ? t("WITHDRAWAL_FORM.COMPLETED_TEXT") : reason || t("WITHDRAWAL_FORM.FAILED_TEXT")}
        </p>
        <button className="alfred-modal__btn" onClick={onClose}>
          {t(isSuccess ? "WITHDRAWAL_FORM.COMPLETED_CTA" : "WITHDRAWAL_FORM.FAILED_CTA")}
        </button>
      </div>
    </div>
  );
};

const DevSimulator: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState<"success" | "failure" | null>(null);
  useEffect(() => {
    let cancelled = false;
    alfredService
      .getDevStatuses()
      .then(() => {
        if (!cancelled) setAvailable(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const fire = async (scenario: "success" | "failure") => {
    setBusy(scenario);
    try {
      await alfredService.devEmitOfframp({ session_id: sessionId, scenario });
    } catch (e) {
      console.error("Dev emit failed:", e);
    } finally {
      setBusy(null);
    }
  };
  if (!available) return null;
  return (
    <details className="alfred-dev-simulator">
      <summary className="alfred-dev-simulator__title">{t("WITHDRAWAL_FORM.DEV_SIMULATOR_TITLE")}</summary>
      <div className="alfred-dev-simulator__content">
        <button
          className="alfred-dev-simulator__btn alfred-dev-simulator__btn--success"
          onClick={() => fire("success")}
          disabled={busy !== null}
        >
          {busy === "success" ? t("WITHDRAWAL_FORM.DEV_EMIT_BUSY") : t("WITHDRAWAL_FORM.DEV_SIM_SUCCESS")}
        </button>
        <button
          className="alfred-dev-simulator__btn alfred-dev-simulator__btn--failure"
          onClick={() => fire("failure")}
          disabled={busy !== null}
        >
          {busy === "failure" ? t("WITHDRAWAL_FORM.DEV_EMIT_BUSY") : t("WITHDRAWAL_FORM.DEV_SIM_FAILURE")}
        </button>
      </div>
    </details>
  );
};

/**
 * Post-submit panel for Alfred withdrawals. Renders the From/To
 * amount summary, the on-chain depositAddress (where the dispatcher
 * sends USDC under the hood), the live offramp stepper, and a
 * fullscreen completion modal once the chain reaches a terminal
 * state. On dev exchanges the backend auto-fires the success chain
 * so the user sees progression without any further action.
 */
const AlfredWithdrawalInstructions: React.FC<Props> = ({ sessionId, transaction, onReset }): JSX.Element => {
  const { t } = useTranslation();
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const snapshot = useAlfredSessionPolling(sessionId, pollingEnabled);
  const status = snapshot?.transactionStatus ?? transaction.status ?? null;
  const [acknowledgedCompletion, setAcknowledgedCompletion] = useState(false);

  useEffect(() => {
    const lang = snapshot?.language;
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => undefined);
    }
  }, [snapshot?.language]);

  const isTerminal = status != null && TERMINAL_STATUSES.has(status);
  useEffect(() => {
    if (isTerminal) setPollingEnabled(false);
  }, [isTerminal]);

  const completionVariant: "success" | "failure" | null = !isTerminal ? null : status === "FAILED" ? "failure" : "success";
  const showCompletionModal = completionVariant !== null && !acknowledgedCompletion;

  const handleCompletionClose = () => {
    setAcknowledgedCompletion(true);
    onReset();
  };

  return (
    <>
      <section className="alfred-instructions">
        <header className="alfred-instructions__header">
          <h2 className="alfred-instructions__title">
            {t("WITHDRAWAL_FORM.WITHDRAWING_TO", {
              currency: transaction.toCurrency,
            })}
          </h2>
        </header>

        <div className="alfred-instructions__amount-block">
          <Row
            label={t("WITHDRAWAL_FORM.AMOUNT_SEND")}
            value={`${formatAmount(transaction.fromAmount)} ${transaction.fromCurrency}`}
            copyable
          />
          <Row
            label={t("WITHDRAWAL_FORM.AMOUNT_RECEIVE")}
            value={`${formatAmount(transaction.toAmount)} ${transaction.toCurrency}`}
            copyable
          />
        </div>

        {transaction.depositAddress && (
          <div className="alfred-instructions__body">
            <Row label={t("WITHDRAWAL_FORM.DEPOSIT_ADDRESS")} value={transaction.depositAddress} copyable />
            <Row label={t("WITHDRAWAL_FORM.CHAIN")} value={transaction.chain} />
          </div>
        )}

        <p className="alfred-instructions__note">{t("WITHDRAWAL_FORM.DISPATCH_NOTE")}</p>

        <StatusBanner status={status} />
        <TransactionStepper
          status={status}
          failedAtStep={snapshot?.failedAtStep ?? null}
          failureReason={snapshot?.failureReason ?? null}
        />

        <DevSimulator sessionId={sessionId} />
      </section>

      {showCompletionModal && completionVariant && (
        <CompletionModal
          variant={completionVariant}
          amount={completionVariant === "success" ? formatAmount(transaction.toAmount) : undefined}
          currency={completionVariant === "success" ? transaction.toCurrency : undefined}
          reason={snapshot?.failureReason ?? null}
          onClose={handleCompletionClose}
        />
      )}
    </>
  );
};

export default AlfredWithdrawalInstructions;
